import { useParams } from 'react-router-dom';
import { useCoachActivityContract } from '@/data/coachActivity';
import { useCoachSelf } from '@/data/coach';

const Clause = ({ id, content, subClauses }) => (
  <div className="mb-2">
    <div className="flex mb-2">
      <span className="w-6 font-bold">({id})</span>
      <span>{content}</span>
    </div>
    {subClauses &&
      subClauses.map((subClause) => (
        <div key={subClause.id} className="ml-6 flex">
          <span className="w-6">({subClause.id})</span>
          <span>{subClause.content}</span>
        </div>
      ))}
  </div>
);

const Subsection = ({ id, title, content, clauses }) => (
  <div className="mb-4">
    <span className="mb-2">
      {id} {title}
    </span>
    {content && <p className="mb-2">{content}</p>}
    {clauses && (
      <div className="ml-4">
        {clauses.map((clause) => (
          <Clause key={clause.id} {...clause} />
        ))}
      </div>
    )}
  </div>
);

const Section = ({ id, title, subsections }) => (
  <div className="mb-6">
    <h3 className="text-lg font-bold mb-3">
      {id}. {title}
    </h3>
    {subsections.map((subsection) => (
      <Subsection key={subsection.id} {...subsection} />
    ))}
  </div>
);

const ContractorAgreement = () => {
  const params = useParams();
  const coach = useCoachSelf();
  const coachActivityContract = useCoachActivityContract(
    params.coachActivityId
  );

  return (
    <div className="py-4 text-sm mb-6">
      <p className="mb-4">DATED as of the {new Date().toLocaleDateString()}</p>

      <div className="mb-6">
        <p className="font-bold mb-2">BETWEEN:</p>
        <p className="ml-4 mb-4">
          ProFound Professional Inc., a company incorporated in the Province of
          British Columbia, with an address of: c/o 200-537 Leon Avenue,
          Kelowna, British Columbia V1Y 2A9
          <br />
          (the &quot;Company&quot;)
        </p>

        <p className="font-bold mb-2">AND:</p>
        <p className="ml-4 mb-4">
          {coach.data.user.firstname} {coach.data.user.lastname}, an independent
          contractor with an address of:
          <br />
          {coach.data.addresses[0].address}
          {coach.data.addresses[0].address2 && (
            <>
              <br />
              {coach.data.addresses[0].address2}
            </>
          )}
          <br />
          {coach.data.addresses[0].city}, {coach.data.addresses[0].stateCode}{' '}
          {coach.data.addresses[0].zipCode}
          <br />
          {coach.data.addresses[0].countryCode}
          <br />
          (the &quot;Contractor&quot;)
        </p>

        <p className="font-bold mb-2">WHEREAS:</p>
        <p className="ml-4 mb-2">
          A. The Company operates an online booking system for clients of the
          Company (the &quot;Clients&quot;) to book the services of trained
          professional coaches, guides, instructors and other professionals
          located in British Columbia (the &quot;Business&quot;);
        </p>
        <p className="ml-4 mb-2">
          B. The Company wishes to engage the services of the Contractor to
          provide coaching / instructing and/or guiding services to the
          Company&apos;s Clients, which services will be booked through the
          Company&apos;s online booking system, and the Contractor wishes to
          provide such services for the Company; and
        </p>
        <p className="ml-4 mb-4">
          C. The Contractor and the Company have reached an agreement respecting
          the terms and conditions under which the Contractor agrees to provide
          its services to the Company.
        </p>

        <p className="mb-4">
          NOW THEREFORE THIS AGREEMENT WITNESSES THAT in consideration of the
          mutual covenants and representations herein contained, and for other
          good and valuable consideration, the receipt and sufficiency of which
          is hereby acknowledged by both parties, the Company and the Contractor
          agree as follows:
        </p>
      </div>
      {coachActivityContract.data.sections.map((section) => (
        <Section key={section.id} {...section} />
      ))}
      <div className="space-y-6 mb-8">
        <h2 className="text-xl font-bold text-center">
          SCHEDULE &quot;A&quot;
        </h2>

        <div className="space-y-4">
          <p className="font-semibold">
            The Contractor shall provide the following Services to the Company:
          </p>

          <ol className="list-decimal pl-6 space-y-4">
            <li>{coachActivityContract.data.servicesContractStr}</li>

            <li>
              The Contractor shall provide the Services to the Clients of the
              Company at the time, place, and in the manner specified in the
              booking on the Company&apos;s online booking system.
            </li>

            <li>
              The Contractor shall be completely ready to begin the session at
              least 10 minutes before the scheduled start time to ensure the
              prompt provision of Services to the Clients and shall provide the
              Services for the entire duration of the booking.
            </li>

            <li>
              The Contractor shall provide the Services in a professional,
              ethical, friendly, responsible manner that enhances the reputation
              of the Contractor and the Company.
            </li>

            <li>
              The Contractor will not at any time disparage the Company or speak
              badly of the Company or any of its agents to the Clients of the
              Company.
            </li>
          </ol>

          <p className="font-semibold mt-6">
            The Contractor possesses, and will continue to maintain during the
            term of this Agreement, the following qualifications, licences, and
            approvals:
          </p>

          <ol className="list-decimal pl-6">
            {coachActivityContract.data.qualifications.map((qual) => (
              <li key={qual.id}>{qual.content}</li>
            ))}
          </ol>
        </div>
      </div>
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-center">
          SCHEDULE &quot;B&quot;
        </h2>

        <div className="space-y-4">
          <ol className="list-decimal pl-6 space-y-4">
            <li>
              The Contractor and the Company acknowledge and agree that
              Contractors may create offerings of Services at rates the
              Contractor determines based on the value of their offerings in the
              marketplace, and that Contractors may have different offerings of
              Services at different rates (a <strong>“Base Rate”</strong>).
            </li>

            <li>
              The Company will add certain additional fees to the Base Rate (the{' '}
              <strong>“Additional Fees”</strong>) such that all costs incurred
              by the Company in relation to the provision of the Services by the
              Contractor shall be paid by the Clients, and without limiting the
              generality of the foregoing shall include:
              <ol className="list-[lower-alpha] pl-6 space-y-2 pt-2">
                <li>
                  an activity fee calculated at a rate of 15% of the Base Rate;
                </li>
                <li>a credit card processing fee;</li>
                <li>an insurance fee; and</li>
                <li>
                  any additional costs that directly relate to the provision of
                  the Services by the Contractor to the Client and paid by the
                  Company.
                </li>
              </ol>
            </li>

            <li>
              The Company may, in the discretion of the Company, round up the
              subtotal of the Base Rate and the Additional Fees to the nearest
              $5 increment.
            </li>

            <li>
              The Contractor and the Company acknowledge and agree that the rate
              of any of the Additional Fees may be changed from time to time,
              and may vary per Service, in the sole discretion of the Company,
              at all times acting reasonably.
            </li>
            <li>
              All taxes, including PST and GST, will be calculated on and added
              to the subtotal of Base Rate plus Additional Fees, for a total
              rate charged to the Client (the “Client Rate”).
            </li>
            <li>
              Within Thirty (30) business days following the Company’s receipt
              of payment of a Client Rate (the “Payment”) for a booking of any
              Service(s) with the Contractor, the Company will distribute the
              Payment as follows:
              <ol className="list-[lower-alpha] pl-6 space-y-2 pt-2">
                <li>
                  the Additional Fees shall be deducted and retained by the
                  Company for its own use absolutely; and
                </li>
                <li>
                  the balance of the Payment shall be distributed directly to
                  the Contractor, which amount will, at all times, equal the
                  Base Rate set by the Contractor at the time of the offering of
                  the Service.
                </li>
              </ol>
            </li>
          </ol>
        </div>
      </div>
      <h3 className="font-bold mt-6">ProFound</h3>
      <p className="font-bold">Empowering Coaches for a Professional Future</p>
    </div>
  );
};

export default ContractorAgreement;
